import { useEffect, useState, type FormEvent } from "react";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";
import { useAdminSession } from "../AdminSessionContext";

interface AdminUserRow {
  id: number;
  email: string;
  created_at: string;
  last_login_at: string | null;
}

export default function AdminUsers() {
  const { admin } = useAdminSession();
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    const res = await fetch(`${API_BASE}/admin-users`);
    const json = await res.json();
    setUsers(json);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/admin-users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setError(json?.error || "No se pudo crear el usuario");
        return;
      }
      setEmail("");
      setPassword("");
      await load();
    } catch {
      setError("Error de conexión.");
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("¿Eliminar este administrador?")) return;
    const res = await fetch(`${API_BASE}/admin-users/${id}`, { method: "DELETE" });
    if (!res.ok) {
      const json = await res.json().catch(() => null);
      alert(json?.error || "No se pudo eliminar");
      return;
    }
    await load();
  }

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Usuarios administradores</h1>
      <p className="mt-1 text-sm text-foreground-500">Máximo 3 cuentas. Todas tienen los mismos permisos.</p>

      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 divide-y divide-background-200">
        {loading ? (
          <p className="p-4 text-sm text-foreground-500">Cargando...</p>
        ) : (
          users.map((u) => (
            <div key={u.id} className="flex items-center justify-between p-4">
              <div>
                <p className="text-sm font-medium text-foreground-900">
                  {u.email} {u.id === admin?.id && <span className="text-xs text-foreground-400">(tú)</span>}
                </p>
                <p className="text-xs text-foreground-500">
                  Creado el {new Date(u.created_at).toLocaleDateString("es-CL")}
                  {u.last_login_at && ` · último ingreso ${new Date(u.last_login_at).toLocaleDateString("es-CL")}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(u.id)}
                className="text-sm text-accent-600 hover:text-accent-700 cursor-pointer"
              >
                Eliminar
              </button>
            </div>
          ))
        )}
      </div>

      {users.length < 3 && (
        <form onSubmit={handleCreate} className="mt-8 rounded-lg border border-background-200 bg-background-50 p-6 space-y-4">
          <h2 className="font-heading font-semibold text-foreground-900">Agregar administrador</h2>
          {error && (
            <div className="rounded-md bg-accent-100 border border-accent-200 p-3 text-sm text-accent-800">{error}</div>
          )}
          <div>
            <label htmlFor="new-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Email
            </label>
            <input
              id="new-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Contraseña (mínimo 8 caracteres)
            </label>
            <input
              id="new-password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <button
            type="submit"
            disabled={creating}
            className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition disabled:opacity-60 cursor-pointer"
          >
            {creating ? "Creando..." : "Crear usuario"}
          </button>
        </form>
      )}
    </AdminLayout>
  );
}
